import firebase from "firebase/compat/app";

import { CoachMarkID, SimpleCoachMarkID } from "../coachMarks/coachMarkListing";
import { CardHandle } from "../components/Card";
import { ReviewAreaMarkingRecord } from "../components/ReviewArea";
import {
  CardStates,
  hasSeenCoachMarkID,
  recordReviewMarking,
  recordSeenCoachMarkID,
  UserRecord,
  UserState,
} from "../db";
import {
  getLevelForInterval,
  SpacedRepetitionSchedule,
} from "../spacedRepetition";
import updateCardStateForReviewMarking from "./updateCardStateForReviewMarking";
import { efficacyExperimentCardIDSet } from "../experiments/efficacy2";

interface CoachMarkStateSpec {
  targetElement: HTMLElement;
  coachMarkID: CoachMarkID;
  isDisappearing: boolean;
  onDismiss: () => void;
}

export interface ReviewCollectionState {
  initialCardStates: CardStates;
  localCardStates: CardStates;
  userRecord: UserRecord | null;
  userState: UserState;
  coachMarkStateSpec: CoachMarkStateSpec | null;
}

export const defaultReviewCollectionState = {
  localCardStates: {},
  userRecord: null,
  coachMarkStateSpec: null,
};

export function getCurrentCardStates(
  initialCardStates: CardStates,
  localCardStates: CardStates,
) {
  return {
    ...initialCardStates,
    ...localCardStates,
  };
}

export function getLocallyCompletedCardCount(state: ReviewCollectionState) {
  return Object.keys(state.localCardStates).filter(
    cardID => !state.localCardStates[cardID].needsRetry,
  ).length;
}

export function getRetryCardIDQueue(state: ReviewCollectionState) {
  const currentCardStates = getCurrentCardStates(
    state.initialCardStates,
    state.localCardStates,
  );
  return [...Object.keys(currentCardStates)]
    .filter(cardID => currentCardStates[cardID].needsRetry)
    .sort((a, b) => {
      const stateA = currentCardStates[a];
      const stateB = currentCardStates[b];
      if (stateA.orderSeed && stateB.orderSeed) {
        return stateA.orderSeed - stateB.orderSeed;
      } else {
        return stateA.dueTime.toMillis() - stateB.dueTime.toMillis();
      }
    });
}

type ReviewCollectionStateUpdate = Omit<
  Partial<ReviewCollectionState>,
  "userRecord"
> & {
  userRecord?: UserRecord;
};

export function handleReviewAreaMarking(args: {
  state: ReviewCollectionState;
  markingRecord: ReviewAreaMarkingRecord;
  cardHandle: CardHandle;
  schedule: SpacedRepetitionSchedule;
  retryCoachMarkID: SimpleCoachMarkID;
  markingMetadata:
    | { essayID: string; essayVersion: number }
    | { sessionID: number };
  repeat: () => void;
}): ReviewCollectionStateUpdate {
  const {
    state,
    markingRecord: {
      task: {
        cardData: { cardID, cardType },
        promptIndex,
        cardState,
      },
      reviewMarking,
    },
    cardHandle,
    schedule,
    retryCoachMarkID,
    markingMetadata,
    repeat,
  } = args;

  const reviewDate = new Date();
  let newCardStateOrderSeed = Math.random();

  if (
    efficacyExperimentCardIDSet.has(cardID) &&
    (!cardState || !cardState.bestInterval)
  ) {
    // HACK: Make sure these cards are in the control group's first review session.
    newCardStateOrderSeed -= 0.9;
  }

  const newCardState = updateCardStateForReviewMarking({
    baseCardState: cardState,
    cardType: cardType,
    reviewMarking: reviewMarking,
    schedule: schedule,
    reviewTimestampMillis: reviewDate.getTime(),
    fromMillis: firebase.firestore.Timestamp.fromMillis,
    generatedOrderSeed: newCardStateOrderSeed,
  });

  const newState: ReviewCollectionStateUpdate = {};

  if (
    reviewMarking === "forgotten" &&
    !hasSeenCoachMarkID(retryCoachMarkID, state.userRecord) &&
    newCardState.needsRetry
  ) {
    const newUserRecord = recordSeenCoachMarkID(
      retryCoachMarkID,
      state.userRecord,
      state.userState,
    );

    const levelToRetry = getLevelForInterval(newCardState.interval, schedule);

    newState.coachMarkStateSpec = {
      coachMarkID: retryCoachMarkID,
      targetElement: cardHandle.getLevelItemElements()![levelToRetry],
      isDisappearing: false,
      onDismiss: () => {
        repeat();
      },
    };
    if (newUserRecord) {
      newState.userRecord = newUserRecord;
    }
  } else {
    if (cardID.startsWith("temp")) {
      console.warn(
        `Not storing review for card "${cardID}" because it hasn't been registered: run "yarn run registerQuestions".`,
      );
    } else {
      recordReviewMarking({
        cardID,
        promptIndex,
        reviewMarking,
        oldCardState: cardState,
        newCardStateOrderSeed,
        reviewDate,
        essayName:
          ("essayID" in markingMetadata && markingMetadata.essayID) || null,
        essayVersion:
          "essayVersion" in markingMetadata &&
          typeof markingMetadata.essayVersion === "number"
            ? markingMetadata.essayVersion
            : null,
        sessionID:
          ("sessionID" in markingMetadata && markingMetadata.sessionID) || null,
      });

      newState.localCardStates = {
        ...state.localCardStates,
        [cardID]: newCardState,
      };
    }
  }
  return newState;
}
